import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { accent } from '../utils/colors';
import { updateSkill, deleteSkill, planSkillLearning } from '../utils/api';
import PlanLearningModal from './PlanLearningModal';

const SkillDetails = ({ selectedNode, apiBase, onSkillUpdated, onSkillDeleted, rootSkills = [] }) => {
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState(selectedNode?.title || '');
  const [description, setDescription] = useState(selectedNode?.description || '');
  const [departmentId, setDepartmentId] = useState(selectedNode?.departmentId ?? 0);
  const [parentId, setParentId] = useState(selectedNode?.parentId ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);

  React.useEffect(() => {
    setTitle(selectedNode?.title || '');
    setDescription(selectedNode?.description || '');
    setDepartmentId(selectedNode?.departmentId ?? 0);
    setParentId(selectedNode?.parentId ?? null);
    setError(null);
    setEditMode(false);
    setShowDeleteConfirm(false);
  }, [selectedNode]);

  if (!selectedNode) {
    return (
      <div style={{ color: accent, fontSize: 22, textAlign: "center", margin: 40 }}>
        Выберите элемент дерева
      </div>
    );
  }

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const skill = {
        id: selectedNode.id,
        parentId: parentId,
        departmentId: departmentId,
        title,
        description,
        level: selectedNode.level,
        orderNum: selectedNode.orderNum,
        hasChildren: selectedNode.hasChildren,
      };
      await updateSkill(apiBase, skill);
      setEditMode(false);
      if (onSkillUpdated) onSkillUpdated({ ...selectedNode, ...skill });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError(null);
    try {
      console.log('Удаляем навык с ID:', selectedNode.id);
      const result = await deleteSkill(apiBase, selectedNode.id);
      console.log('Результат удаления:', result);
      setShowDeleteConfirm(false);
      if (onSkillDeleted) onSkillDeleted(selectedNode.id);
    } catch (e) {
      console.error('Ошибка при удалении:', e);
      setError(e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <h2 style={{ 
        color: accent, 
        marginBottom: 18, 
        fontSize: 30, 
        fontWeight: 600, 
        wordBreak: "break-word", 
        lineHeight: 1.2 
      }}>
        {editMode ? (
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={{ fontSize: 24, fontWeight: 600, color: accent, width: '100%', padding: 6, borderRadius: 6, border: `1.5px solid ${accent}` }}
            disabled={loading}
          />
        ) : (
          selectedNode.title || selectedNode.description || `ID ${selectedNode.id || ''}`
        )}
        {!editMode && (
        <div style={{ fontSize: 14, color: '#666', fontWeight: 400, marginTop: 4 }}>
          Department ID: {selectedNode.departmentId}
        </div>
        )}
        <div style={{ display: 'inline-block', marginLeft: 16 }}>
          <button
            style={{ fontSize: 16, background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 500, marginRight: 8 }}
            onClick={() => setEditMode(e => !e)}
            disabled={loading || deleteLoading}
          >
            {editMode ? 'Отмена' : 'Редактировать'}
          </button>
          {/* Запланировать изучение */}
          {!editMode && (
            <button
              style={{ fontSize: 16, background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 500, marginRight: 8 }}
              onClick={() => setShowPlanModal(true)}
              disabled={loading || deleteLoading || planLoading}
            >
              Запланировать изучение
            </button>
          )}
          {/* Кнопка удаления только для навыков без дочерних элементов */}
          {!editMode && !selectedNode.hasChildren && (
            <button
              style={{ fontSize: 16, background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 500 }}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading || deleteLoading}
            >
              Удалить
            </button>
          )}
        </div>
      </h2>

      {/* Диалог подтверждения удаления */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            padding: 24,
            borderRadius: 12,
            maxWidth: 400,
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#dc3545', marginBottom: 16 }}>Подтверждение удаления</h3>
            <p style={{ marginBottom: 24, fontSize: 16 }}>
              Вы уверены, что хотите удалить навык "{selectedNode.title}"?
            </p>
            <div>
              <button
                onClick={handleDelete}
                style={{
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 18px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginRight: 10
                }}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Удаление...' : 'Удалить'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 18px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                disabled={deleteLoading}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка планирования изучения */}
      <PlanLearningModal
        isOpen={showPlanModal}
        onClose={() => {
          if (!planLoading) {
            setShowPlanModal(false);
            setError(null);
          }
        }}
        onSubmit={async (crmId) => {
          setPlanLoading(true);
          setError(null);
          try {
            await planSkillLearning(apiBase, selectedNode.id, crmId);
            setShowPlanModal(false);
            setError(null);
            // Можно добавить уведомление об успехе, если есть система нотификаций
          } catch (e) {
            setError(e.message || 'Ошибка при планировании изучения');
          } finally {
            setPlanLoading(false);
          }
        }}
        loading={planLoading}
        apiError={error}
      />

      {editMode ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#333' }}>Id департамента</label>
            <input
              type="number"
              value={departmentId}
              onChange={e => setDepartmentId(parseInt(e.target.value, 10) || 0)}
              style={{ width: '100%', fontSize: 16, borderRadius: 6, border: `1.5px solid ${accent}`, padding: 8 }}
              disabled={loading}
              placeholder="Department ID"
            />
          </div>
          {editMode && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#333' }}>
                Родительский навык
              </label>
              <select
                value={parentId ?? ''}
                onChange={e => setParentId(e.target.value ? parseInt(e.target.value) : null)}
                style={{ width: '100%', fontSize: 16, borderRadius: 6, border: `1.5px solid ${accent}`, padding: 8 }}
                disabled={loading}
              >
                <option value="">Без родителя (корневой)</option>
                {rootSkills.map(skill => (
                  <option key={skill.id} value={skill.id}>
                    {skill.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#333' }}>Описание</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={25}
              style={{ 'minWidth':'100%', width: '100%', fontSize: 16, borderRadius: 6, border: `1.5px solid ${accent}`, padding: 8 }}
              disabled={loading}
              placeholder="Описание навыка"
            />
          </div>
          <div>
            <button
              onClick={handleSave}
              style={{ background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 16, fontWeight: 600, cursor: 'pointer', marginRight: 10 }}
              disabled={loading}
            >
              {loading ? 'Сохраняю...' : 'Сохранить'}
            </button>
            {error && <span style={{ color: 'red', marginLeft: 10 }}>{error}</span>}
          </div>
        </>
      ) : selectedNode.description ? (
        <div className="markdown-block">
          <ReactMarkdown 
            components={{
              h1: ({children}) => <h1 style={{color: accent, fontSize: '1.8em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h1>,
              h2: ({children}) => <h2 style={{color: accent, fontSize: '1.5em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h2>,
              h3: ({children}) => <h3 style={{color: accent, fontSize: '1.3em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h3>,
              p: ({children}) => <p style={{marginBottom: '1em'}}>{children}</p>,
              ul: ({children}) => <ul style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ul>,
              ol: ({children}) => <ol style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ol>,
              li: ({children}) => <li style={{marginBottom: '0.5em'}}>{children}</li>,
              code: ({children}) => <code style={{background: '#f3eaff', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.9em'}}>{children}</code>,
              pre: ({children}) => <pre style={{background: '#f3eaff', padding: '1em', borderRadius: 8, overflow: 'auto', marginBottom: '1em'}}>{children}</pre>,
              blockquote: ({children}) => <blockquote style={{borderLeft: `4px solid ${accent}`, paddingLeft: '1em', marginLeft: 0, color: '#6a1b9a', fontStyle: 'italic'}}>{children}</blockquote>,
              strong: ({children}) => <strong style={{fontWeight: 600, color: accent}}>{children}</strong>,
              em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
              a: ({children, href}) => <a href={href} style={{color: accent, textDecoration: 'none'}} target="_blank" rel="noopener noreferrer">{children}</a>
            }}
          >
            {selectedNode.description.replace(/\r\n|\r/g, '\n').replace(/^[ \t]+/gm, '')}
          </ReactMarkdown>
        </div>
      ) : (
        <div style={{ color: "#888", fontSize: 18, marginTop: 30 }}>Нет описания</div>
      )}
    </>
  );
};

export default SkillDetails; 